<template>
  <div
    class="flex h-12 w-full items-center gap-2 border-b border-neutral-200 px-3 md:hidden"
    style="background-color: rgb(247, 244, 236)"
  >
    <div class="flex min-w-0 flex-1 items-center gap-3">
      <a
        class="flex min-w-0 cursor-pointer items-center gap-2"
        data-test="employee-mobile-project"
        @click="is_menu_open = true"
      >
        <FontAwesomeIcon
          :icon="['far', 'bars']"
          size="lg"
          class="shrink-0 text-gray-600"
        />
        <Avatar
          :src="left_avatar_src"
          :username="left_label"
          format="square"
          size="32"
        />
        <span class="truncate text-base font-bold text-gray-800">
          {{ left_label }}
        </span>
      </a>

      <a
        v-if="active_pill"
        class="flex cursor-pointer items-center gap-2 rounded-full border border-neutral-300 bg-white px-3 py-1.5 shadow-sm"
        :data-test="active_pill.data_test"
        @click="togglePillMenu"
      >
        <span class="truncate text-base font-semibold text-gray-800">
          {{ active_pill.label }}
        </span>
        <FontAwesomeIcon
          :icon="['far', 'chevron-down']"
          class="shrink-0 text-gray-700"
        />
      </a>
    </div>

    <NotificationsToggleIcon class="shrink-0" />

    <PMenu
      ref="sub_tab_menu_ref"
      :model="sub_tab_items"
      :popup="true"
    />

    <PMenu
      ref="feature_menu_ref"
      :model="navbar_items"
      :popup="true"
    >
      <template #item="{ item, props }">
        <NavbarItemHorizontal
          :item="item"
          :params="params"
          v-bind="props.action"
        />
      </template>
    </PMenu>
  </div>
  <Sidebar v-model="is_menu_open" />
</template>

<script setup>
import { computed, getCurrentInstance, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import Sidebar from '@/app/_employees/components/navbar/TheSidebarEmployee.vue';
import NavbarItemHorizontal from '@/app/shared_components/navbar/NavbarItemHorizontal.vue';
import NotificationsToggleIcon from '@/app/shared_components/navbar/notifications/TheNotificationsToggleIcon.vue';
import {
  NAVBAR_ITEMS_EMPLOYEES_OVERVIEW_NAVBAR,
  NAVBAR_ITEMS_EMPLOYEES_SECONDARY_NAVBAR,
} from '@/app/data/navbar_constants';
import { PROFILE_TYPES } from '@/app/data/model_constants';
import { ROUTES_EMPLOYEES } from '@/app/data/route_constants';
import { FT } from '@/app/data/flipper_constants';
import { mapGetters } from '@/store/lib/vue3helpers';

const SETTINGS_TABS = [
  { name: ROUTES_EMPLOYEES.CLIENT_COMPANY, label: 'client.labels.tab_company' },
  { name: ROUTES_EMPLOYEES.SETTINGS_TEAM, label: 'navbar.topnavbar.team' },
  { name: ROUTES_EMPLOYEES.CLIENT_SETTINGS, label: 'client.labels.tab_settings' },
  { name: ROUTES_EMPLOYEES.CLIENT_SUBSCRIPTION, label: 'client.labels.tab_subscription' },
  { name: ROUTES_EMPLOYEES.CLIENT_INTEGRATIONS, label: 'client.labels.tab_integrations' },
];

const { proxy: app } = getCurrentInstance();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const { current_client, current_project } = mapGetters(['current_client', 'current_project']);

const is_menu_open = ref(false);
const feature_menu_ref = ref(null);
const sub_tab_menu_ref = ref(null);

const left_label = computed(() => {
  if (current_project.value && current_project.value.attributes && current_project.value.attributes.name) {
    return current_project.value.attributes.name;
  }
  if (current_client.value && current_client.value.attributes && current_client.value.attributes.name) {
    return current_client.value.attributes.name;
  }
  return '';
});

const left_avatar_src = computed(() => {
  if (
    current_project.value &&
    current_project.value.attributes &&
    current_project.value.attributes.picture_login_url_s
  ) {
    return current_project.value.attributes.picture_login_url_s;
  }
  if (current_client.value && current_client.value.attributes) {
    return current_client.value.attributes.picture_logo_url_s;
  }
  return undefined;
});

const isFlipperEnabled = (item) => !item.flipper || app.$flipper.enabled(item.flipper);

const navbar_items = computed(() => {
  if (current_project.value) {
    return NAVBAR_ITEMS_EMPLOYEES_SECONDARY_NAVBAR.filter(
      (item) =>
        isFlipperEnabled(item) && (!current_project.value.attributes.skip_customer || !item.hidden_skip_customer),
    );
  }
  return NAVBAR_ITEMS_EMPLOYEES_OVERVIEW_NAVBAR.filter(isFlipperEnabled);
});

const active_feature = computed(() =>
  navbar_items.value.find((item) => route.name && route.name.startsWith(item.prefix)),
);

const contacts_tabs = computed(() => {
  const tabs = [PROFILE_TYPES.CUSTOMER];
  if (app.$flipper.enabled(FT.SALES)) tabs.push(PROFILE_TYPES.LEAD);
  if (app.$flipper.enabled(FT.DECISIONS_PARTNERS) || app.$flipper.enabled(FT.PROJECT_PARTNERS)) {
    tabs.push(PROFILE_TYPES.PARTNER);
  }
  return tabs;
});

const sub_tabs = computed(() => {
  if (route.name === ROUTES_EMPLOYEES.CLIENT_CONTACTS_TYPE) {
    const active_key = route.params.profile_type || PROFILE_TYPES.CUSTOMER;
    return {
      label: t(`contact.labels.${active_key.toLowerCase()}`),
      items: contacts_tabs.value.map((key) => ({
        label: t(`contact.labels.${key.toLowerCase()}`),
        command: () => router.push({ name: ROUTES_EMPLOYEES.CLIENT_CONTACTS_TYPE, params: { profile_type: key } }),
      })),
    };
  }
  const active_settings_tab = SETTINGS_TABS.find((tab) => route.name && route.name.startsWith(tab.name));
  if (active_settings_tab) {
    return {
      label: t(active_settings_tab.label),
      items: SETTINGS_TABS.map((tab) => ({
        label: t(tab.label),
        command: () => router.push({ name: tab.name }),
      })),
    };
  }
  return null;
});

const active_pill = computed(() => {
  if (sub_tabs.value) {
    return { label: sub_tabs.value.label, data_test: 'employee-mobile-sub-tab', is_sub_tab: true };
  }
  if (active_feature.value) {
    return { label: t(active_feature.value.title), data_test: 'employee-mobile-feature', is_sub_tab: false };
  }
  return null;
});

const sub_tab_items = computed(() => (sub_tabs.value ? sub_tabs.value.items : []));

const params = computed(() => {
  if (!current_project.value) return {};
  return {
    project_id: current_project.value.id,
    level_type: current_project.value.type,
    level_id: current_project.value.id,
  };
});

const togglePillMenu = (event) => {
  const target = active_pill.value && active_pill.value.is_sub_tab ? sub_tab_menu_ref.value : feature_menu_ref.value;
  if (target) target.toggle(event);
};
</script>
